import { CwtApiEntity, CwtCrudContract, CwtModel, CwtProp } from "@cawita/core-front/api";
import { isset } from "@hints/utils/data";
import { differenceInHours, differenceInMinutes, differenceInSeconds } from "date-fns";
import { Client, Machine, Site } from "../client";
import { ConsentValue } from "../common/consent.model";
import { CustomFieldValues } from "../custom-field";
import type { CompanyAnswers } from "./company-answers.model";
import type { EvalCerebral } from "./eval-cerebral.model";
import type { EvalEmotional } from "./eval-emotional.model";
import type { EvalEnergetical } from "./eval-energetical.model";
import type { EvalMental } from "./eval-mental.model";
import type { EvalPhysical } from "./eval-physical.model";
import type { EvaluationResultEntity } from "./eval-result.entity";
import type { EvalSleep } from "./eval-sleep.model";
import { EvalThermalCure } from "./eval-thermal-cure.model";
import { EvaluationRecommendation } from "./recommendation.model";
import { Tester } from "./tester.model";

export enum EvaluationStatus {
    Pending = 'pending',
    Done = 'done'
}

@CwtModel({ name: 'evaluation' })
@CwtCrudContract({ path: 'evaluation', searchMapper: (search) => ({ search }) })
@CwtCrudContract({ path: 'for-manager/client/:clientId/evaluation', identifier: 'for-manager', searchMapper: (search) => ({ search }) })
@CwtCrudContract({ path: 'for-machine/evaluation', identifier: 'for-machine' })
export class Evaluation extends CwtApiEntity {
    // Infos
    @CwtProp({ ref: Tester }) tester: Tester;
    @CwtProp() client: Client;
    @CwtProp({ ref: Site }) site: Site;
    @CwtProp({ ref: Machine }) machine: Machine;
    @CwtProp({ ref: Date }) date: Date;
    @CwtProp({ ref: Date }) doneDate: Date;
    @CwtProp() status: EvaluationStatus;
    
    // Results
    @CwtProp({ ref: 'evaluation' }) origin: Evaluation;
    @CwtProp({ ref: 'eval-sleep' }) evalSleep: EvalSleep;
    @CwtProp({ ref: 'eval-physical' }) evalPhysical: EvalPhysical;
    @CwtProp({ ref: 'eval-emotional' }) evalEmotional: EvalEmotional;
    @CwtProp({ ref: 'eval-mental' }) evalMental: EvalMental;
    @CwtProp({ ref: 'eval-energetical' }) evalEnergetical: EvalEnergetical;
    @CwtProp({ ref: 'eval-cerebral' }) evalCerebral: EvalCerebral;
    @CwtProp({ ref: EvaluationRecommendation }) recommendation: EvaluationRecommendation;

    // Consents
    @CwtProp({ ref: ConsentValue }) energeticalConsent: ConsentValue;
    @CwtProp({ ref: ConsentValue }) mentalConsent: ConsentValue;
    @CwtProp({ ref: ConsentValue }) shareConsent: ConsentValue;

    // Features
    @CwtProp() isCompanyQuestionEvaluation: boolean;
    @CwtProp() isThermalCureEvaluation: boolean;
    
    // Addons
    @CwtProp() customFieldValues: CustomFieldValues;
    @CwtProp({ ref: 'company-answers' }) companyAnswers: CompanyAnswers;
    @CwtProp({ ref: EvalThermalCure }) thermalCure: EvalThermalCure;
    // Computed

    public get hasGivenEnergeticalConsent() { return !isset(this.energeticalConsent) || this.energeticalConsent.hasGivenConsent; }
    public get hasGivenMentalConsent() { return !isset(this.mentalConsent) || this.mentalConsent.hasGivenConsent; }
    public get hasGivenShareConsent() { return isset(this.shareConsent) && this.shareConsent.hasGivenConsent; }
    public get hasAnsweredShareConsent() { return this.shareConsent?.hasAnsweredConsent; }

    public get isThermalCure() { return this.thermalCure?.hasRecos; }
    public get isPartial(): boolean { return isset(this.origin); }
    public get isDone(): boolean { return this.status === EvaluationStatus.Done; }
    public get isPending(): boolean { return this.status === EvaluationStatus.Pending; }
    
    public get duration() { return differenceInSeconds(this.doneDate, this.date);}

    public canUseForPartialTest(): boolean {
        const date = this.doneDate ?? this.date;
        const diff = Math.abs(differenceInHours(date, new Date()));
        return diff < 72;
    }
}

export type EvaluationMode = 'training' | 'real';
export type EvaluationType = 'sleep' | 'physical' | 'emotional' | 'energetical' | 'mental' | 'cerebral';
export type EvaluationField = { [K in keyof Evaluation]-?: Evaluation[K] extends EvaluationResultEntity ? K : never; }[keyof Evaluation];

export const EvaluationNames: Record<EvaluationType, EvaluationField> = {
    'sleep': 'evalSleep',
    'physical': 'evalPhysical',
    'emotional': 'evalEmotional',
    'energetical': 'evalEnergetical',
    'mental': 'evalMental',
    'cerebral': 'evalCerebral',
};

export const ReverseEvaluationNames: Record<EvaluationField, EvaluationType> = {
    'evalSleep': 'sleep',
    'evalPhysical': 'physical',
    'evalEmotional': 'emotional',
    'evalEnergetical': 'energetical',
    'evalMental': 'mental',
    'evalCerebral': 'cerebral',
};

export const EvaluationTypeOrderInDashboard: EvaluationType[] = ['sleep', 'physical', 'emotional', 'energetical', 'mental', 'cerebral'];
export const EvaluationTypeOrderInEvaluation: EvaluationType[] = ['sleep', 'physical', 'emotional', 'energetical', 'mental', 'cerebral'];
export const EvaluationTypeOrderInRadarChart: EvaluationType[] = ['physical', 'emotional', 'sleep', 'mental', 'cerebral', 'energetical'];
export const EvaluationTypeOrderInStatistics: EvaluationType[] = ['sleep', 'physical', 'emotional', 'energetical', 'mental', 'cerebral'];